import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate } from 'react-router-dom'

import { pocetnaZaUlogu } from '../app/rute'
import { LanguageSwitcher } from '../components/Jezik'
import { odjavi, promijeniLozinku } from '../auth/session'
import { useSession } from '../auth/useSession'
import { Button } from '../components/Button'
import { TextField } from '../components/Field'
import { DNO_EKRANA, VRH_EKRANA } from '../components/ljuska'
import { porukaGreske, porukaPolja } from '../i18n/greske'
import { Obrazac } from '../components/Obrazac'

/** Donja granica je duljina, ne sastav (ChangeStaffPasswordCommandValidator). */
const NAJMANJE_ZNAKOVA = 10

/**
 * `POST /auth/staff/change-password` (API §1).
 *
 * Dok stoji claim `pwd_change`, poslužitelj svaki zahtjev izvan `/auth/*` vraća
 * s 403 `password_change_required` — brana je middleware, ne popis endpointa
 * (CLAUDE.md §16, odluka 6). Ovaj ekran je zato jedini put dalje, a ne jedan od
 * ponuđenih. Odgovor nosi NOVI par tokena, jer se claim iz već izdanog tokena ne
 * može povući unatrag; `session.ts` ga preuzima i obveza nestaje sama.
 *
 * Nakon uspjeha korisnik OSTAJE prijavljen: par tokena iz odgovora je svježa
 * sesija, pa bi slanje na prijavu značilo baciti ga i tražiti lozinku koju je
 * upravo odabrao. Potvrda se ipak pokazuje — tiho preusmjeravanje ne kaže je li
 * promjena prošla, a ekran usput javlja i da su ostale sesije odjavljene.
 */
export function ChangePasswordPage() {
  const { t } = useTranslation()
  const sesija = useSession()

  const [stara, postaviStaru] = useState('')
  const [nova, postaviNovu] = useState('')
  const [potvrda, postaviPotvrdu] = useState('')
  const [greska, postaviGresku] = useState<unknown>(null)
  const [neslaganje, postaviNeslaganje] = useState(false)
  const [salje, postaviSlanje] = useState(false)
  const [promijenjeno, postaviPromijenjeno] = useState(false)

  if (!sesija.accessToken) return <Navigate to="/login" replace />

  // Prije provjere obveze: promjenom je obveza pala, pa bi inače potvrdu pregazilo
  // preusmjeravanje na početnu.
  if (promijenjeno) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-noir px-4">
        <div className="w-full max-w-sm rounded-2xl border border-line bg-surface p-6 text-center">
          <h1 className="text-2xl text-ivory">{t('auth.changePassword.title')}</h1>
          <p role="status" className="mt-2 text-sm text-champagne">
            {t('auth.changePassword.done')}
          </p>

          <Link
            to={pocetnaZaUlogu(sesija.uloga)}
            replace
            className="touch-target mt-6 inline-flex w-full items-center justify-center rounded-lg bg-brass px-4 py-2 text-sm font-semibold text-noir transition-colors hover:bg-champagne"
          >
            {t('auth.changePassword.nastavi')}
          </Link>
        </div>
      </div>
    )
  }

  if (!sesija.moraPromijenitiLozinku) return <Navigate to={pocetnaZaUlogu(sesija.uloga)} replace />

  async function posalji(dogadaj: FormEvent) {
    dogadaj.preventDefault()
    postaviGresku(null)

    if (nova !== potvrda) {
      postaviNeslaganje(true)
      return
    }

    postaviNeslaganje(false)
    postaviSlanje(true)

    try {
      await promijeniLozinku(stara, nova)
      postaviPromijenjeno(true)
    } catch (kvar) {
      postaviGresku(kvar)
    } finally {
      postaviSlanje(false)
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-noir">
      <div className="flex justify-end px-4 pb-4" style={{ paddingTop: VRH_EKRANA }}>
        <LanguageSwitcher />
      </div>

      <main className="flex grow items-start justify-center px-4" style={{ paddingBottom: DNO_EKRANA }}>
        <Obrazac
          naSlanje={(dogadaj) => void posalji(dogadaj)}
          className="w-full max-w-sm rounded-2xl border border-line bg-surface p-6"
        >
          <h1 className="text-center text-2xl text-ivory">{t('auth.changePassword.title')}</h1>
          <p className="mt-2 mb-6 text-sm text-stone">{t('auth.changePassword.lead')}</p>

          <div className="flex flex-col gap-4 [&_label]:text-brass [&_input]:border-line [&_input]:bg-noir [&_input]:text-ivory [&_p]:text-stone">
            <TextField
              label={t('auth.changePassword.stara')}
              type="password"
              value={stara}
              onChange={(dogadaj) => postaviStaru(dogadaj.target.value)}
              autoComplete="current-password"
              required
            />

            <TextField
              label={t('auth.changePassword.nova')}
              hint={t('auth.changePassword.hint', { count: NAJMANJE_ZNAKOVA })}
              greska={porukaPolja(t, greska, 'nova_lozinka')}
              type="password"
              value={nova}
              onChange={(dogadaj) => postaviNovu(dogadaj.target.value)}
              minLength={NAJMANJE_ZNAKOVA}
              autoComplete="new-password"
              required
            />

            <TextField
              label={t('auth.changePassword.potvrda')}
              greska={neslaganje ? t('auth.changePassword.mismatch') : undefined}
              type="password"
              value={potvrda}
              onChange={(dogadaj) => postaviPotvrdu(dogadaj.target.value)}
              autoComplete="new-password"
              required
            />
          </div>

          {greska ? (
            <p role="alert" className="mt-4 text-sm text-champagne">
              {porukaGreske(t, greska)}
            </p>
          ) : null}

          <Button izgled="gold" type="submit" className="mt-6 w-full" disabled={salje}>
            {salje ? t('common.saving') : t('auth.changePassword.submit')}
          </Button>

          <button
            type="button"
            onClick={() => void odjavi()}
            className="mt-4 w-full text-center text-xs text-stone hover:text-champagne"
          >
            {t('nav.logout')}
          </button>
        </Obrazac>
      </main>
    </div>
  )
}
